import React, { useMemo, useState } from "react";
import {
  Modal,
  Table,
  TableColumnsType,
  Typography,
  Descriptions,
  Divider,
  Input,
} from "antd";
import {
  IPartNumberDtoExportTransfer,
  IWarehouseExportTransfer,
  IWarehouseLocation,
} from "../../../models/Warehouse";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useExportWarehouseTransfer } from "../../../query/WarehouseQuery";
import { notify } from "../../../utils/toast";
import { IWarehouseLocationDetail } from "../../../models/Warehouse";

interface ModalPreviewExportTransferProps {
  open: boolean;
  selectedItems: Array<IPartNumberDtoExportTransfer>;
  onOk: () => void;
  onCancel: () => void;
}

const ModalPreviewExportTransfer: React.FC<ModalPreviewExportTransferProps> = ({
  open,
  selectedItems,
  onOk,
  onCancel,
}) => {
  const locations = useSelector(
    (state: RootState) => state.warehouse.locations,
  ) as IWarehouseLocation[];
  const [note, setNote] = useState<string>("");
  const [receiver, setReceiver] = useState<string>("");

  const totalQuantity = useMemo(
    () => selectedItems.reduce((sum, it) => sum + (it.exportQuantity || 0), 0),
    [selectedItems],
  );
  const exportTransferMutation = useExportWarehouseTransfer();
  const columns: TableColumnsType<IPartNumberDtoExportTransfer> = [
    {
      title: "STT",
      dataIndex: "index",
      align: "center",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "AWB",
      dataIndex: "awb",
      align: "center",
    },
    {
      title: "Dự án",
      dataIndex: "project",
      align: "center",
    },
    {
      title: "Mã hàng",
      dataIndex: "partNumber",
      align: "center",
    },
    {
      title: "Từ",
      key: "from",
      align: "center",
      children: [
        {
          title: "Dự án",
          dataIndex: "project",
          align: "center",
        },
        {
          title: "Mã kho",
          dataIndex: ["location", "warehouse", "warehouseCode"],
          align: "center",
        },
        {
          title: "Vị trí",
          dataIndex: ["location", "code"],
          align: "center",
        },
      ],
    },
    {
      title: "Đến",
      key: "to",
      align: "center",
      children: [
        {
          title: "Dự án",
          dataIndex: "targetProject",
          align: "center",
        },
        {
          title: "Mã kho",
          dataIndex: "targetWarehouseId",
          align: "center",
          render: (v: number) =>
            locations.find((loc) => loc.id === v)?.warehouseCode || "",
        },
        {
          title: "Vị trí",
          dataIndex: "targetLocationId",
          align: "center",
          render: (v: number, record) => {
            const warehouse = locations.find(
              (loc) => loc.id === record.targetWarehouseId,
            );
            const location = warehouse?.warehouseLocations.find(
              (loc: IWarehouseLocationDetail) => loc.id === v,
            );
            return location ? location.code : "";
          },
        },
      ],
    },
    {
      title: "Số lượng xuất",
      dataIndex: "exportQuantity",
      align: "center",
      render: (v: number) => (
        <span className="font-medium text-blue-600">{v}</span>
      ),
    },
    {
      title: "Đơn vị",
      dataIndex: "unit",
      align: "center",
      render: (value: string) =>
        value === "pcs" || value === "PCS"
          ? "Cái"
          : value === "set"
            ? "Bộ"
            : value,
    },
  ];

  const handleConfirm = async () => {
    if (!receiver || receiver.trim() === "") {
      notify.error("Vui lòng nhập tên người nhận");
      return;
    }

    const missing = selectedItems.filter(
      (it) => !it.targetWarehouseId || !it.targetLocationId,
    );
    if (missing.length > 0) {
      notify.error(
        "Một số mã hàng chưa chọn kho hoặc vị trí đích. Vui lòng kiểm tra lại.",
      );
      return;
    }
    const data = selectedItems.map((it) => ({
      id: it.id,
      exportQuantity: it.exportQuantity,
      targetLocationId: it.targetLocationId!,
      targetProject: it.targetProject!,
      targetProjectCode: it.targetProjectCode,
    }));
    const exportData: IWarehouseExportTransfer = {
      exportTransfers: data,
      note: note,
      receiver: receiver.trim(),
    };
    try {
      await exportTransferMutation.mutateAsync(exportData);
      notify.success("Điều chuyển kho thành công");
      onOk();
    } catch (error) {
      notify.error("Có lỗi xảy ra. Vui lòng thử lại.");
    }
  };

  return (
    <>
      <Modal
        open={open}
        title={
          <div className="flex items-center gap-2">
            <Typography.Title
              level={4}
              style={{
                margin: 0,
                fontFamily: "Chakra Petch",
                textTransform: "uppercase",
              }}
            >
              Thông tin điều chuyển
            </Typography.Title>
            <Typography.Text className="text-gray-600">
              (Kiểm tra kỹ trước khi xác nhận)
            </Typography.Text>
          </div>
        }
        width="70%"
        onOk={handleConfirm}
        onCancel={onCancel}
        okText="Xác nhận điều chuyển"
        cancelText="Đóng"
        centered
      >
        <div className="space-y-6">
          <Descriptions bordered size="small">
            <Descriptions.Item label="Ngày" span={3}>
              {new Date().toLocaleString("vi-VN", { dateStyle: "full" })}
            </Descriptions.Item>
          </Descriptions>
          <Divider orientation="left">Chi tiết điều chuyển</Divider>
          <Table
            size="small"
            pagination={false}
            columns={columns}
            dataSource={selectedItems}
            rowKey="id"
            bordered
            style={{
              borderRadius: 8,
              overflow: "hidden",
            }}
            className="table-bold-border"
            summary={() => (
              <Table.Summary fixed="bottom">
                <Table.Summary.Row style={{ fontWeight: "bold" }}>
                  <Table.Summary.Cell index={0} colSpan={10} align="right">
                    <strong>Tổng cộng:</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <span className="font-medium text-blue-600">
                      {totalQuantity}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          <Input
            placeholder="Nhập tên người nhận"
            value={receiver}
            onChange={(e) => setReceiver(e.target.value)}
            status={receiver.trim() === "" ? "error" : ""}
            required
          />
          <Input.TextArea
            rows={3}
            placeholder="Ghi chú (nếu có)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </Modal>
    </>
  );
};

export default ModalPreviewExportTransfer;
